/**
 * Shop Local Nav Web Component
 * ----------------------------
 * Category mega-menu local navigation bar for the shop.
 *
 * Carries the sticky scroll state machine,
 * body-level backdrop overlay, dropdown hover timer logic, Escape key handler,
 * active-category underline, and dropdowns rendered as siblings of <header>.
 *
 * Attributes:
 * - categories: JSON array of { slug, label, categories[] } objects
 * - active-category: Category slug or null
 * - extension-href: URL for browser extension install CTA
 *
 * Events dispatched:
 * - shop-local-nav-category-click { detail: { category: string } }
 * - shop-local-nav-extension-click
 */

import { escapeHTML } from "../../../shared/escape_html.js";

const ShopLocalNav = (function () {
  const TAG_NAME = "shop-local-nav";

  // Feature flag: enable the floating pill state on scroll.
  // Set to true to re-enable the condensed pill treatment when the user
  // scrolls down (Design is still iterating on this state).
  const ENABLE_FLOATING_PILL = false;

  /**
   * Safely parses JSON, returning null on failure.
   */
  function safeParseJSON(jsonStr) {
    try {
      return JSON.parse(jsonStr);
    } catch {
      return null;
    }
  }

  /**
   * Sanitizes a URL to prevent javascript:/data: injection.
   * Allows relative paths (starting with /) and http/https URLs.
   * Falls back to "#" for anything else.
   */
  function sanitizeHref(url) {
    if (typeof url !== "string") return "#";
    const trimmed = url.trim();
    if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return "#";
  }

  class ShopLocalNav extends HTMLElement {
    static get observedAttributes() {
      return ["categories", "active-category", "extension-href"];
    }

    constructor() {
      super();
    }

    connectedCallback() {
      if (!this._initialized) {
        this._injectStyles();
        this._renderContent();
        this._initialized = true;
        this._attachGlobalListeners();
        this._attachContentListeners();
      }
      // Re-create the body-level backdrop on every (re)connect; it is removed
      // in disconnectedCallback. Kept out of _attachContentListeners so it does
      // not run on every attribute-change re-render.
      this._ensureDropdownBackdrop();
    }

    disconnectedCallback() {
      this._detachGlobalListeners();
      this._detachContentListeners();
      this._removeDropdownBackdrop();
    }

    /**
     * The dropdown backdrop sits at <body> level (sibling of <shop-local-nav>)
     * so its `backdrop-filter` blurs the page content behind it without
     * affecting the nav layers inside shop-local-nav's stacking context.
     */
    _ensureDropdownBackdrop() {
      if (this._dropdownBackdrop && this._dropdownBackdrop.isConnected) return;
      let existing = document.body.querySelector(
        ":scope > .shop-local-nav__dropdown-backdrop",
      );
      if (!existing) {
        existing = document.createElement("div");
        existing.className = "shop-local-nav__dropdown-backdrop";
        existing.setAttribute("hidden", "");
        existing.setAttribute("aria-hidden", "true");
        document.body.appendChild(existing);
      }
      this._dropdownBackdrop = existing;
    }

    _removeDropdownBackdrop() {
      if (this._dropdownBackdrop && this._dropdownBackdrop.isConnected) {
        this._dropdownBackdrop.remove();
      }
      this._dropdownBackdrop = null;
    }

    attributeChangedCallback(_name, oldValue, newValue) {
      if (oldValue === newValue || !this._initialized) return;
      // Cancel any pending hover-out close timer before re-rendering — a
      // 150ms close timer left running against the freshly-rendered DOM
      // would fire against the new dropdown and close a menu the user just
      // opened (e.g. active-category updating mid-hover).
      if (this._dropdownCloseTimer) {
        clearTimeout(this._dropdownCloseTimer);
        this._dropdownCloseTimer = null;
        this._dropdownBackdrop?.setAttribute("hidden", "");
        this.classList.remove("is-menu-open");
        const gn = document.querySelector("fragno-global-nav");
        if (gn) gn.classList.remove("is-overlay-active");
      }
      this._renderContent();
      this._attachContentListeners();
    }

    /**
     * Attach global listeners (scroll, document-level Escape) — called once in
     * connectedCallback. These do not need to be re-attached on attribute changes.
     */
    _attachGlobalListeners() {
      // Sticky-header scroll behavior + close dropdowns on scroll.
      //
      // Desktop only: when scrolling down, the global nav slides out of view
      // and the local nav remains sticky as a condensed pill. Scrolling up
      // reveals the global nav again. Mobile/tablet keeps the existing
      // non-sticky layout.

      // Threshold = the fragno-global-nav's rendered height. Falls back to 80
      // (5rem at the default 16px root) if the nav isn't found.
      const globalNavEl = document.querySelector("fragno-global-nav");
      const SCROLL_THRESHOLD = globalNavEl?.offsetHeight || 80;
      const SCROLL_DELTA = 5; // ignore tiny scroll changes
      const DESKTOP_MIN_WIDTH = 1024;
      this._lastScrollY = window.scrollY;
      this._onScroll = () => {
        if (window.innerWidth < DESKTOP_MIN_WIDTH) {
          this.classList.remove("is-scrolled-down");
          this._lastScrollY = window.scrollY;
          return;
        }

        const currentScrollY = window.scrollY;
        const delta = currentScrollY - this._lastScrollY;

        // Ignore phantom/no-op scroll events (e.g. fired by the browser when
        // a fixed-position child changes layout). Without this, opening a
        // dropdown could immediately trigger close logic.
        if (Math.abs(delta) < SCROLL_DELTA) return;

        // Real scroll: close any open category dropdowns since they are
        // position:fixed and don't track the local nav as it scrolls.
        const openDropdowns = this.querySelectorAll(
          ".shop-local-nav__category-dropdown:not([hidden])",
        );
        if (openDropdowns.length) {
          openDropdowns.forEach((d) => d.setAttribute("hidden", ""));
          this.querySelectorAll(
            ".shop-local-nav__category-btn[aria-expanded='true']",
          ).forEach((b) => b.setAttribute("aria-expanded", "false"));
          this.querySelectorAll(
            ".shop-local-nav__category-item--expanded",
          ).forEach((i) =>
            i.classList.remove("shop-local-nav__category-item--expanded"),
          );
          this.classList.remove("is-menu-open");
          const gn = document.querySelector("fragno-global-nav");
          if (gn) gn.classList.remove("is-overlay-active");
          // Only hide backdrop if search isn't also open
          if (!gn?.classList.contains("is-search-open")) {
            this._dropdownBackdrop?.setAttribute("hidden", "");
          }
        }

        if (ENABLE_FLOATING_PILL) {
          if (currentScrollY <= SCROLL_THRESHOLD) {
            this.classList.remove("is-scrolled-down");
          } else if (delta > 0) {
            this.classList.add("is-scrolled-down");
          }
        }

        this._lastScrollY = currentScrollY;
      };
      window.addEventListener("scroll", this._onScroll, { passive: true });

      // Escape key handler for dropdowns (desktop only — nav is display:none
      // on mobile so there is nothing to dismiss).
      this._onEscapeKey = this._handleEscapeKey.bind(this);
      document.addEventListener("keydown", this._onEscapeKey);
    }

    /**
     * Attach content-level listeners (category click, hover, keyboard) —
     * called after every _renderContent since innerHTML destroys old DOM nodes.
     */
    _attachContentListeners() {
      // Category buttons (click) — store per-button refs to avoid the leak
      // where a shared this._onCategoryClick is reassigned per iteration.
      const categoryBtns = this.querySelectorAll(
        ".shop-local-nav__category-btn",
      );
      this._categoryClickRefs = [];
      categoryBtns.forEach((btn) => {
        const handler = this._handleCategoryClick.bind(this);
        btn.addEventListener("click", handler);
        this._categoryClickRefs.push({ btn, handler });
      });

      // Extension button
      const extensionBtn = this.querySelector(".shop-local-nav__extension-btn");
      if (extensionBtn) {
        this._onExtensionClick = this._handleExtensionClick.bind(this);
        extensionBtn.addEventListener("click", this._onExtensionClick);
      }

      // Desktop category items with dropdowns (hover)
      const categoryItems = this.querySelectorAll(
        ".shop-local-nav__category-item--with-dropdown",
      );

      const showDropdownBackdrop = () => {
        if (this._dropdownBackdrop) {
          this._dropdownBackdrop.style.top =
            this.getBoundingClientRect().bottom + "px";
          this._dropdownBackdrop.removeAttribute("hidden");
        }
      };

      const hideDropdownBackdrop = () => {
        this._dropdownBackdrop?.setAttribute("hidden", "");
      };

      // True if any dropdown is currently open (visible).
      const anyDropdownOpen = () =>
        !!this.querySelector(
          ".shop-local-nav__category-dropdown:not([hidden])",
        );

      // Reflect the open/closed mega-menu state on the host element. Used in
      // CSS to drop the floating-pill treatment in the scrolled-down state when
      // a mega menu is open, so the local nav reads as a flat bar attached to
      // the dropdown beneath it.
      const syncMenuOpenClass = () => {
        const globalNavEl = document.querySelector("fragno-global-nav");
        if (anyDropdownOpen()) {
          this.classList.add("is-menu-open");
          if (globalNavEl) globalNavEl.classList.add("is-overlay-active");
        } else {
          this.classList.remove("is-menu-open");
          if (globalNavEl) globalNavEl.classList.remove("is-overlay-active");
        }
      };

      // Close every dropdown EXCEPT the one we're switching to. Backdrop is
      // intentionally not toggled here — moving from one item to another
      // should swap the open dropdown without flashing the backdrop off.
      const closeOtherDropdowns = (keepDropdown) => {
        const keepSlug = keepDropdown?.getAttribute("data-category");
        this.querySelectorAll(
          ".shop-local-nav__category-dropdown:not([hidden])",
        ).forEach((d) => {
          if (d !== keepDropdown) d.setAttribute("hidden", "");
        });
        this.querySelectorAll(
          ".shop-local-nav__category-btn[aria-expanded='true']",
        ).forEach((b) => {
          if (b.getAttribute("data-category") !== keepSlug) {
            b.setAttribute("aria-expanded", "false");
          }
        });
        this.querySelectorAll(
          ".shop-local-nav__category-item--expanded",
        ).forEach((i) => {
          const btn = i.querySelector(".shop-local-nav__category-btn");
          if (btn?.getAttribute("data-category") !== keepSlug) {
            i.classList.remove("shop-local-nav__category-item--expanded");
          }
        });
        syncMenuOpenClass();
      };

      // Close every dropdown and hide the backdrop. Used when the mouse
      // genuinely leaves the nav/dropdown area.
      const closeAllDropdowns = () => {
        this.querySelectorAll(".shop-local-nav__category-dropdown").forEach(
          (d) => d.setAttribute("hidden", ""),
        );
        this.querySelectorAll(
          ".shop-local-nav__category-btn[aria-expanded='true']",
        ).forEach((b) => b.setAttribute("aria-expanded", "false"));
        this.querySelectorAll(
          ".shop-local-nav__category-item--expanded",
        ).forEach((i) =>
          i.classList.remove("shop-local-nav__category-item--expanded"),
        );
        hideDropdownBackdrop();
        syncMenuOpenClass();
      };

      // Close timer is shared across all items so that hovering one item
      // cancels the pending close from the previous one.
      const cancelCloseTimer = () => {
        if (this._dropdownCloseTimer) {
          clearTimeout(this._dropdownCloseTimer);
          this._dropdownCloseTimer = null;
        }
      };

      const scheduleClose = () => {
        cancelCloseTimer();
        this._dropdownCloseTimer = setTimeout(() => {
          closeAllDropdowns();
          this._dropdownCloseTimer = null;
        }, 150);
      };

      categoryItems.forEach((item) => {
        const btn = item.querySelector(".shop-local-nav__category-btn");
        const slug = btn?.getAttribute("data-category");
        const dropdown = slug
          ? this.querySelector(
              `.shop-local-nav__category-dropdown[data-category="${slug}"]`,
            )
          : null;
        if (!btn || !dropdown) return;

        const openDropdown = () => {
          cancelCloseTimer();
          closeOtherDropdowns(dropdown);
          dropdown.removeAttribute("hidden");
          btn.setAttribute("aria-expanded", "true");
          item.classList.add("shop-local-nav__category-item--expanded");
          const barEl = this.querySelector(".shop-local-nav__bar");
          const pillEl = this.querySelector(".shop-local-nav__categories-nav");
          if (barEl) {
            dropdown.style.top = `${barEl.getBoundingClientRect().bottom}px`;
          }
          // In pill state, constrain the dropdown to the pill's width/position
          if (this.classList.contains("is-scrolled-down") && pillEl) {
            const pillRect = pillEl.getBoundingClientRect();
            dropdown.style.left = `${pillRect.left}px`;
            dropdown.style.right = "auto";
            dropdown.style.width = `${pillRect.width}px`;
            dropdown.classList.add("shop-local-nav__category-dropdown--pill");
          } else {
            dropdown.style.left = "";
            dropdown.style.right = "";
            dropdown.style.width = "";
            dropdown.classList.remove(
              "shop-local-nav__category-dropdown--pill",
            );
          }
          showDropdownBackdrop();
          syncMenuOpenClass();
        };

        // Close just this dropdown. The backdrop remains visible if another
        // dropdown is still open; only fully closes when nothing is open.
        const closeThisDropdown = () => {
          dropdown.setAttribute("hidden", "");
          btn.setAttribute("aria-expanded", "false");
          item.classList.remove("shop-local-nav__category-item--expanded");
          if (!anyDropdownOpen()) hideDropdownBackdrop();
          syncMenuOpenClass();
        };

        // The item only opens the dropdown on enter — leave-detection is
        // handled at the bar level (below) so dead space between buttons does
        // not trigger a close.
        const onMouseEnter = () => {
          cancelCloseTimer();
          openDropdown();
        };

        // Keyboard: Enter/Space to toggle
        const onKeyDown = (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            const isExpanded = btn.getAttribute("aria-expanded") === "true";
            if (isExpanded) closeThisDropdown();
            else openDropdown();
          } else if (
            e.key === "Escape" &&
            btn.getAttribute("aria-expanded") === "true"
          ) {
            // Dismiss the open mega menu and return focus to its trigger
            // (ARIA disclosure-navigation pattern / WCAG 2.1 SC 1.4.13).
            closeThisDropdown();
            btn.focus();
          }
        };

        // Attach hover and keyboard listeners
        item.addEventListener("mouseenter", onMouseEnter);
        btn.addEventListener("keydown", onKeyDown);

        // Store references for cleanup
        item._onMouseEnter = onMouseEnter;
        btn._onKeyDown = onKeyDown;
      });

      // Close-on-leave is wired to the bar (and each dropdown) rather than
      // per-item, so moving the mouse between buttons over the gap inside the
      // nav does not close the open dropdown. The menu only closes when the
      // mouse genuinely leaves the nav and the dropdown.
      const barEl = this.querySelector(".shop-local-nav__bar");
      if (barEl) {
        barEl._onMouseEnter = () => cancelCloseTimer();
        barEl._onMouseLeave = () => scheduleClose();
        barEl.addEventListener("mouseenter", barEl._onMouseEnter);
        barEl.addEventListener("mouseleave", barEl._onMouseLeave);
      }

      // Each dropdown keeps its own enter/leave handler so the menu stays
      // open while the mouse is over the mega menu, and closes shortly after
      // it moves outside.
      this.querySelectorAll(".shop-local-nav__category-dropdown").forEach(
        (dropdown) => {
          dropdown._onMouseEnter = () => cancelCloseTimer();
          dropdown._onMouseLeave = () => scheduleClose();
          dropdown.addEventListener("mouseenter", dropdown._onMouseEnter);
          dropdown.addEventListener("mouseleave", dropdown._onMouseLeave);
        },
      );
    }

    /**
     * Detach global listeners (scroll, document-level Escape).
     * Called only in disconnectedCallback.
     */
    _detachGlobalListeners() {
      if (this._onEscapeKey) {
        document.removeEventListener("keydown", this._onEscapeKey);
        this._onEscapeKey = null;
      }

      if (this._onScroll) {
        window.removeEventListener("scroll", this._onScroll);
        this._onScroll = null;
      }
    }

    /**
     * Detach content-level listeners. Called in disconnectedCallback (and
     * implicitly unnecessary on re-render since innerHTML destroys the nodes).
     */
    _detachContentListeners() {
      // Category click listeners (per-button refs)
      if (this._categoryClickRefs) {
        this._categoryClickRefs.forEach(({ btn, handler }) => {
          btn.removeEventListener("click", handler);
        });
        this._categoryClickRefs = null;
      }

      const extensionBtn = this.querySelector(".shop-local-nav__extension-btn");
      if (extensionBtn && this._onExtensionClick) {
        extensionBtn.removeEventListener("click", this._onExtensionClick);
      }

      // Clean up hover listeners on category items
      this.querySelectorAll(
        ".shop-local-nav__category-item--with-dropdown",
      ).forEach((item) => {
        if (item._onMouseEnter)
          item.removeEventListener("mouseenter", item._onMouseEnter);
        if (item._onMouseLeave) {
          item.removeEventListener("mouseleave", item._onMouseLeave);
          item._onMouseLeave = null;
        }
        const btn = item.querySelector(".shop-local-nav__category-btn");
        if (btn?._onKeyDown) btn.removeEventListener("keydown", btn._onKeyDown);
      });

      // Clean up bar-level and dropdown-level hover listeners
      const barEl = this.querySelector(".shop-local-nav__bar");
      if (barEl?._onMouseEnter) {
        barEl.removeEventListener("mouseenter", barEl._onMouseEnter);
      }
      if (barEl?._onMouseLeave) {
        barEl.removeEventListener("mouseleave", barEl._onMouseLeave);
      }
      this.querySelectorAll(".shop-local-nav__category-dropdown").forEach(
        (dropdown) => {
          if (dropdown._onMouseEnter) {
            dropdown.removeEventListener("mouseenter", dropdown._onMouseEnter);
          }
          if (dropdown._onMouseLeave) {
            dropdown.removeEventListener("mouseleave", dropdown._onMouseLeave);
          }
        },
      );

      if (this._dropdownCloseTimer) {
        clearTimeout(this._dropdownCloseTimer);
        this._dropdownCloseTimer = null;
      }
    }

    _handleCategoryClick(e) {
      const category = e.currentTarget.getAttribute("data-category");
      this.dispatchEvent(
        new CustomEvent("shop-local-nav-category-click", {
          bubbles: true,
          composed: true,
          detail: { category },
        }),
      );
    }

    _handleExtensionClick() {
      this.dispatchEvent(
        new CustomEvent("shop-local-nav-extension-click", {
          bubbles: true,
          composed: true,
        }),
      );
    }

    _handleEscapeKey(e) {
      if (e.key === "Escape") {
        // Early return if no dropdown is actually open — avoids unnecessary
        // DOM queries on mobile where the nav is display:none.
        if (
          !this.querySelector(
            ".shop-local-nav__category-dropdown:not([hidden])",
          )
        )
          return;
        const openBtn = this.querySelector(
          ".shop-local-nav__category-btn[aria-expanded='true']",
        );
        // Close any open category dropdowns
        this.querySelectorAll(
          ".shop-local-nav__category-dropdown:not([hidden])",
        ).forEach((d) => d.setAttribute("hidden", ""));
        this.querySelectorAll(
          ".shop-local-nav__category-btn[aria-expanded='true']",
        ).forEach((b) => b.setAttribute("aria-expanded", "false"));
        this.querySelectorAll(
          ".shop-local-nav__category-item--expanded",
        ).forEach((i) =>
          i.classList.remove("shop-local-nav__category-item--expanded"),
        );
        this.classList.remove("is-menu-open");
        const gn = document.querySelector("fragno-global-nav");
        if (gn) gn.classList.remove("is-overlay-active");
        if (!gn?.classList.contains("is-search-open")) {
          this._dropdownBackdrop?.setAttribute("hidden", "");
        }
        openBtn?.focus();
      }
    }

    _getCategories() {
      const categoriesAttr = this.getAttribute("categories");
      if (categoriesAttr) {
        const parsed = safeParseJSON(categoriesAttr);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
      return [];
    }

    _getActiveCategory() {
      return this.getAttribute("active-category") || null;
    }

    _getExtensionHref() {
      return this.getAttribute("extension-href") || "/extension/install";
    }

    /**
     * Inject styles once. Separated from content rendering so that attribute
     * changes do not re-parse the entire ~370-line style block.
     */
    _injectStyles() {
      this._styleEl = document.createElement("style");
      this._styleEl.textContent = `
          shop-local-nav {
            display: block;
            position: relative;
          }

          .shop-local-nav__bar,
          .shop-local-nav__bar-compliance {
            background: var(--colors-white-default, #FFFFFF);
            position: relative;
            z-index: 1;
          }

          .shop-local-nav__bar {
            border-bottom: 1px solid var(--colors-gray-100, #F5F5F5);
          }

          .shop-local-nav__bar .content-block__inner {
            max-width: 90rem;
            margin: 0 auto;
            padding: 0 var(--spacing-8xl, 5rem);
            display: flex;
            align-items: stretch;
            height: 3rem;
            position: relative;
            overflow: visible;
          }

          .shop-local-nav__categories-nav {
            display: flex;
            align-items: stretch;
            justify-content: space-between;
            gap: 1.5rem;
            overflow-x: auto;
            overflow-y: visible;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            width: 100%;
            height: 100%;
            padding-bottom: 3px;
            transition:
              height 0.25s ease-out,
              padding 0.25s ease-out,
              background 0.25s ease-out,
              border-radius 0.25s ease-out,
              box-shadow 0.25s ease-out;
          }

          .shop-local-nav__categories-nav::-webkit-scrollbar {
            display: none;
          }

          .shop-local-nav__category-item {
            position: relative;
            display: flex;
            align-items: stretch;
            flex-shrink: 0;
            align-self: stretch;
            border-bottom: 2px solid transparent;
            transition: border-bottom-color 0.2s ease;
          }

          .shop-local-nav__category-item:hover,
          .shop-local-nav__category-item--active,
          .shop-local-nav__category-item--expanded {
            border-bottom-color: var(--colors-gray-900, #141414);
          }

          .shop-local-nav__category-item:hover .shop-local-nav__category-btn:not(.shop-local-nav__category-btn--active) {
            color: var(--colors-gray-900, #141414);
          }

          .shop-local-nav__category-btn {
            display: inline-flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            padding: 0;
            background: transparent;
            border: 0 solid transparent !important;
            font-size: var(--font-size-sm, 0.875rem);
            font-weight: 500;
            color: #525252;
            cursor: pointer;
            text-decoration: none;
            transition:
              color 0.2s ease,
              border-bottom-color 0.2s ease;
            position: relative;
            white-space: nowrap;
            flex-shrink: 0;
          }

          .shop-local-nav__category-btn::before {
            content: attr(data-label);
            display: block;
            height: 0;
            visibility: hidden;
            overflow: hidden;
            font-weight: var(--font-weight-medium-plus, 550);
            pointer-events: none;
          }

          .shop-local-nav__category-btn--active,
          .shop-local-nav__category-btn[aria-expanded="true"] {
            color: var(--colors-gray-900, #141414);
            font-weight: var(--font-weight-medium-plus, 550);
            border-bottom-color: var(--colors-gray-900, #141414) !important;
          }

          .shop-local-nav__category-btn:focus-visible {
            outline: none;
            box-shadow: inset 0 0 0 2px var(--colors-gray-900, #141414);
          }

          /* Suppress the hover underline when focused so the focus box
             bottom edge reads as a clean rectangle. */
          .shop-local-nav__category-item:has(.shop-local-nav__category-btn:focus-visible) {
            border-bottom-color: transparent !important;
          }

          /* Category Mega Menu */
          .shop-local-nav__category-dropdown {
            position: fixed;
            left: 0;
            right: 0;
            z-index: 95;
            display: block;
            opacity: 1;
            visibility: visible;
            pointer-events: all;
            transition: opacity 0.2s ease;
            background: var(--colors-white-default, #FFFFFF);
            box-shadow: var(--box-shadow-search-dropdown, 0 0.25rem 0.75rem rgba(0, 0, 0, 0.08));
          }

          /* Backdrop overlay shown when a category dropdown is open.
             Rendered as a sibling of shop-local-nav in body (managed by
             _ensureDropdownBackdrop) so its backdrop-filter blurs the page
             content without leaking into shop-local-nav's stacking context.
             Sits just below shop-local-nav and above page content. */
          /* z-index 89: below shop-local-nav (z-90) so the nav bar and its
             dropdowns render above the backdrop, not behind it. The backdrop
             only blurs page content beneath the sticky nav. */
          .shop-local-nav__dropdown-backdrop {
            position: fixed;
            top: 0; /* dynamically set via JS in showDropdownBackdrop */
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.4);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
            z-index: 89;
            display: none;
            pointer-events: none;
          }

          .shop-local-nav__dropdown-backdrop:not([hidden]) {
            display: block;
          }

          .shop-local-nav__category-dropdown[hidden] {
            display: none !important;
            opacity: 0;
          }

          .shop-local-nav__category-dropdown-content {
            max-width: 90rem;
            margin: 0 auto;
            padding: 2.5rem var(--spacing-8xl, 5rem);
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 2.5rem 2rem;
          }

          /* Category Column in Mega Menu */
          .shop-local-nav__category-column {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            min-width: 0;
          }

          .shop-local-nav__category-column-header {
            font-family: var(--font-family-body, 'Inter', sans-serif);
            font-size: 0.875rem;
            font-weight: 600;
            line-height: 1.25rem;
            color: #141414;
            margin-bottom: 0;
          }

          .shop-local-nav__category-column-header-link {
            color: inherit;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
          }

          .shop-local-nav__category-column-header-link:hover {
            color: #0d5c3a;
            text-decoration: underline;
          }

          .shop-local-nav__category-chevron {
            width: 0.75rem;
            height: 0.75rem;
            flex-shrink: 0;
          }

          .shop-local-nav__subcategory-link {
            display: block;
            padding: 0.25rem 0;
            font-family: var(--font-family-body, 'Inter', sans-serif);
            font-size: 0.875rem;
            font-weight: 400;
            line-height: 1.25rem;
            color: #525252;
            text-decoration: none;
            transition: color 0.15s ease;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .shop-local-nav__subcategory-link:hover {
            color: #141414;
            text-decoration: underline;
          }

          /* Extension CTA — hidden pending design finalization. */
          .shop-local-nav__extension-btn {
            display: none !important;
          }

          /* Extension CTA — original styles (kept for when CTA is re-enabled). */
          .shop-local-nav__extension-btn--future {
            position: absolute;
            right: var(--spacing-8xl, 5rem);
            top: 50%;
            transform: translateY(-50%);
            display: inline-flex;
            align-items: center;
            gap: var(--spacing-xs, 0.375rem);
            padding: 0.5rem 0.75rem;
            background: transparent;
            border: 1px solid #a3a3a3;
            border-radius: 999px;
            font-size: 0.875rem;
            font-weight: 500;
            color: #141414;
            cursor: pointer;
            transition: all 0.2s ease;
            text-decoration: none;
          }

          .shop-local-nav__extension-btn:hover {
            background: #f5f5f5;
            border-color: #525252;
          }

          .shop-local-nav__extension-btn:focus {
            outline: 2px solid var(--colors-primary-default, #0066cc);
            outline-offset: 2px;
          }

          .shop-local-nav__extension-btn svg {
            width: 1.25rem;
            height: 1.25rem;
            flex-shrink: 0;
          }

          /* Mobile: hide local nav (categories are in the mobile menu) */
          @media (max-width: 1023px) {
            shop-local-nav {
              display: none !important;
            }
          }

          /* Sticky behavior (desktop only):
             - shop-local-nav sticks to the top of the viewport (positioned
               below fragno-global-nav which also sticks).
             - When the user scrolls down, the host gains .is-scrolled-down
               which triggers the condensed pill treatment.
             - When .is-menu-open is also present, drop the pill so the nav
               reads as a continuous bar with the dropdown beneath it. */
          @media (min-width: 1024px) {
            shop-local-nav {
              ${ENABLE_FLOATING_PILL ? "position: sticky; top: 0;" : "position: relative;"}
              z-index: 91;
              transition: top 0.25s ease-out;
            }

            /* Hide extension CTA in the condensed pill state — it would
               overlap the floating pill and category labels. */
            shop-local-nav.is-scrolled-down .shop-local-nav__extension-btn {
              display: none;
            }

            /* Pill state: the categories nav itself becomes the floating pill. */
            shop-local-nav.is-scrolled-down .shop-local-nav__bar {
              background: transparent;
              border-bottom: none;
            }

            shop-local-nav.is-scrolled-down .shop-local-nav__bar .content-block__inner {
              align-items: center;
              justify-content: center;
            }

            shop-local-nav.is-scrolled-down .shop-local-nav__categories-nav {
              height: 3rem;
              width: auto;
              padding: 0 var(--spacing-2xl, 1.25rem);
              gap: 0.25rem;
              justify-content: center;
              background: rgba(255, 255, 255, 0.85);
              backdrop-filter: blur(30px);
              -webkit-backdrop-filter: blur(30px);
              border-radius: var(--radius-full, 9999px);
              box-shadow:
                0 0.125rem 0.25rem rgba(0, 0, 0, 0.06),
                0 0.25rem 1.125rem 0.5rem rgba(0, 0, 0, 0.06);
            }

            shop-local-nav.is-scrolled-down .shop-local-nav__category-item {
              border-bottom: none;
              align-items: center;
            }

            shop-local-nav.is-scrolled-down .shop-local-nav__category-btn {
              font-size: var(--font-size-xs, 0.8125rem);
              text-decoration: none;
              text-underline-offset: 0.25em;
              text-decoration-thickness: 0.5em;
            }

            shop-local-nav.is-scrolled-down .shop-local-nav__category-item:hover .shop-local-nav__category-btn,
            shop-local-nav.is-scrolled-down .shop-local-nav__category-item--active .shop-local-nav__category-btn,
            shop-local-nav.is-scrolled-down .shop-local-nav__category-item--expanded .shop-local-nav__category-btn {
              text-decoration: underline;
              text-decoration-color: var(--colors-gray-900, #141414);
            }

            /* In pill state, the dropdown matches the pill's width with a
               small gap and rounded corners (mirroring the search results
               box treatment). */
            .shop-local-nav__category-dropdown--pill {
              margin-top: 0.5rem;
              border-radius: var(--radius-5xl, 2rem);
              overflow: hidden;
            }

            .shop-local-nav__category-dropdown--pill .shop-local-nav__category-dropdown-content {
              padding: 1.5rem;
              grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
              gap: 1.5rem 1rem;
            }
          }
      `;
      this.appendChild(this._styleEl);
    }

    /**
     * Re-render only the data-driven markup (not styles).
     */
    _renderContent() {
      const categories = this._getCategories();
      const activeCategory = this._getActiveCategory();
      const extensionHref = sanitizeHref(this._getExtensionHref());

      // Remove all children except the persistent <style> element.
      Array.from(this.childNodes).forEach((node) => {
        if (node !== this._styleEl) node.remove();
      });

      const contentContainer = document.createElement("div");
      contentContainer.innerHTML = `
        <!-- Local Nav Bar -->
        <div class="shop-local-nav__bar">
          <div class="content-block__inner">
            <!-- Category Groups -->
            <nav class="shop-local-nav__categories-nav" role="navigation" aria-label="Category navigation">
              ${categories
                .map((group) => {
                  const hasCategories =
                    Array.isArray(group.categories) &&
                    group.categories.length > 0;
                  return `
                <div class="shop-local-nav__category-item ${hasCategories ? "shop-local-nav__category-item--with-dropdown" : ""} ${activeCategory === group.slug ? "shop-local-nav__category-item--active" : ""}">
                  <button
                    class="shop-local-nav__category-btn ${activeCategory === group.slug ? "shop-local-nav__category-btn--active" : ""}"
                    data-category="${escapeHTML(group.slug)}"
                    data-label="${escapeHTML(group.label)}"
                    ${activeCategory === group.slug ? 'aria-current="page"' : ""}
                    ${hasCategories ? 'aria-haspopup="true" aria-expanded="false"' : ""}
                    type="button"
                  >
                    ${escapeHTML(group.label)}
                  </button>
                </div>
              `;
                })
                .join("")}
            </nav>

            <!-- Extension CTA -->
            <a
              href="${escapeHTML(extensionHref)}"
              class="shop-local-nav__extension-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Get extension
            </a>
          </div>
        </div>

        <!-- Mega menu dropdowns. Rendered inside shop-local-nav but positioned
             fixed so they are not clipped by categories-nav's overflow: auto.
             Each dropdown is paired with its trigger button via data-category. -->
        ${categories
          .filter(
            (group) =>
              Array.isArray(group.categories) && group.categories.length > 0,
          )
          .map((group) => {
            const sorted = [...group.categories].sort((a, b) => {
              const aLen = Array.isArray(a.subcategories)
                ? a.subcategories.length
                : 0;
              const bLen = Array.isArray(b.subcategories)
                ? b.subcategories.length
                : 0;
              return bLen - aLen;
            });
            const withSubs = sorted.filter(
              (c) =>
                Array.isArray(c.subcategories) && c.subcategories.length > 0,
            );
            const withoutSubs = sorted.filter(
              (c) =>
                !Array.isArray(c.subcategories) || c.subcategories.length === 0,
            );

            const categoryHeaderHTML = (cat) => `
              <div class="shop-local-nav__category-column-header">
                <a href="/categories/${escapeHTML(cat.slug)}" class="shop-local-nav__category-column-header-link">
                  ${escapeHTML(cat.label)}
                  <svg class="shop-local-nav__category-chevron" xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="6.5 4 7 12" fill="none" aria-hidden="true">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </a>
              </div>`;

            const withSubsHTML = withSubs
              .map(
                (cat) => `
              <div class="shop-local-nav__category-column">
                ${categoryHeaderHTML(cat)}
                ${cat.subcategories
                  .map(
                    (sub) => `
                  <a href="/categories/${escapeHTML(sub.slug)}" class="shop-local-nav__subcategory-link">
                    ${escapeHTML(sub.label)}
                  </a>
                `,
                  )
                  .join("")}
              </div>
            `,
              )
              .join("");

            const withoutSubsHTML = [];
            for (let i = 0; i < withoutSubs.length; i += 2) {
              const pair = withoutSubs.slice(i, i + 2);
              withoutSubsHTML.push(`
              <div class="shop-local-nav__category-column">
                ${pair.map((cat) => categoryHeaderHTML(cat)).join("")}
              </div>
              `);
            }

            return `
            <div class="shop-local-nav__category-dropdown" data-category="${escapeHTML(group.slug)}" hidden>
              <div class="shop-local-nav__category-dropdown-content">
                ${withSubsHTML + withoutSubsHTML.join("")}
              </div>
            </div>
            `;
          })
          .join("")}
      `;

      // Append all content nodes into the component (after the <style>).
      while (contentContainer.firstChild) {
        this.appendChild(contentContainer.firstChild);
      }
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ShopLocalNav);
  }

  return ShopLocalNav;
})();

export { ShopLocalNav };
